// ============================================================================
// NET INCOME FOOTER COMPONENT (Sprint 2.10)
// ============================================================================
// Displays real-time Net Income calculation pinned to the bottom of the table.
//
// Formula: Net Income = (Sum of all "Income" + "Deductions" accounts) * -1
// Scope: Calculated across ALL accounts in the engagement, ignoring active filters.
// Columns: Book Balance, RJE, AJE, Adj Book, M-Adj, Ftax
//
// Display Rules (System_Foundation.md):
//   - Zero values display as dash (-)
//   - Negative values display in red
//   - Uses displayAmountRounded (integer-cent math)
//
// Layout:
//   - Sticky to bottom of table area
//   - Does NOT scroll horizontally (always visible for tie-out)
//   - Full Light/Dark theme support

import React from 'react';
import { displayAmountRounded } from '../utils/format';

// ============================================================================
// TYPES
// ============================================================================

export interface NetIncomeValues {
  bookBalance: number; // integer cents
  rje: number;
  aje: number;
  adjBook: number;
  mAdj: number;
  ftax: number;
}

interface NetIncomeFooterProps {
  netIncome: NetIncomeValues;
  /** Cell selection summary for the right side of the footer */
  selectionCount: number;
  selectionSum: number;
}

// ============================================================================
// HELPER: Render a single Net Income value
// ============================================================================

const NetIncomeValue: React.FC<{ label: string; cents: number }> = ({ label, cents }) => {
  const { text, isNegative } = displayAmountRounded(cents);

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-gray-500 dark:text-gray-400 font-medium">{label}</span>
      <span
        className={`font-mono font-semibold ${
          isNegative
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {text}
      </span>
    </span>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const NetIncomeFooter: React.FC<NetIncomeFooterProps> = ({
  netIncome,
  selectionCount,
  selectionSum,
}) => {
  // Selection summary rendering
  const renderSelectionSummary = () => {
    if (selectionCount === 0) return null;

    const { text: sumText, isNegative } = displayAmountRounded(selectionSum);

    return (
      <div className="flex items-center gap-3 text-sm">
        <span className="text-gray-500 dark:text-gray-400">COUNT</span>
        <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">
          {selectionCount.toLocaleString()}
        </span>
        <span className="text-gray-300 dark:text-gray-600">|</span>
        <span className="text-gray-500 dark:text-gray-400">SUM</span>
        <span
          className={`font-mono font-semibold ${
            isNegative
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-900 dark:text-gray-100'
          }`}
        >
          {sumText}
        </span>
      </div>
    );
  };

  return (
    <div
      className={
        'flex-shrink-0 border-t-2 border-gray-300 dark:border-gray-600 ' +
        'bg-gray-100 dark:bg-neutral-800 ' +
        'px-4 py-2 ' +
        'flex items-center justify-between gap-4 ' +
        'select-none overflow-hidden'
      }
    >
      {/* ── Left side: NET INCOME label + six column values ────────── */}
      <div className="flex items-center gap-4 text-sm flex-shrink-0 overflow-x-auto hide-scrollbar">
        <span className="font-bold text-gray-900 dark:text-gray-100 tracking-wide whitespace-nowrap">
          NET INCOME:
        </span>

        <NetIncomeValue label="BOOK" cents={netIncome.bookBalance} />
        <NetIncomeValue label="RJE" cents={netIncome.rje} />
        <NetIncomeValue label="AJE" cents={netIncome.aje} />
        <NetIncomeValue label="ADJ BOOK" cents={netIncome.adjBook} />
        <NetIncomeValue label="M-ADJ" cents={netIncome.mAdj} />
        <NetIncomeValue label="FTAX" cents={netIncome.ftax} />
      </div>

      {/* ── Right side: Selection COUNT / SUM ──────────────────────── */}
      <div className="flex-shrink-0">{renderSelectionSummary()}</div>
    </div>
  );
};

export default NetIncomeFooter;
